import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PaymentPendingUserTable from "./PendingUserList";
import { RootState } from "@/lib/store";
import { getPaymentPendingUsers } from "@/services/users";
import toast from "react-hot-toast";
import AdminHeader from "../common/AdminHeader";


export default function PaymentPendingUsers() {
    const {
        loaded: permsLoaded,
        data: permissions,
    } = useSelector((state: RootState) => state.permissions);

    const [paymentPendingUsers, setPaymentPendingUsers] = useState<any[]>([]);

    const [paymentPendingUsersPage, setPaymentPendingUsersPage] =
        useState(1);
    const [loading, setLoading] = useState(false)
    const [
        paymentPendingUsersTotalPages,
        setPaymentPendingUsersTotalPages,
    ] = useState(1);

    const [
        paymentPendingUsersTotalCount,
        setPaymentPendingUsersTotalCount,
    ] = useState(0);

    const [searchQuery, setSearchQuery] = useState("");

    const canSeePaymentPendingUsers =
        !permsLoaded ||
        ("pending payments" in permissions &&
            permissions["pending payments"]?.Can_View !== false);

    const loadPaymentPendingUsers = useCallback(
        async (
            search: string = "",
            page: number = 1
        ) => {
            if (!canSeePaymentPendingUsers) {
                return;
            }

            setLoading(true);

            try {
                const response = await getPaymentPendingUsers(
                    search.trim() || undefined,
                    page
                );

                const d = response?.data as any;

                if (d) {
                    const results = Array.isArray(d)
                        ? d
                        : d.results ?? [];

                    setPaymentPendingUsers(results);

                    setPaymentPendingUsersTotalPages(
                        Array.isArray(d)
                            ? 1
                            : d.total_pages ?? 1
                    );

                    setPaymentPendingUsersTotalCount(
                        Array.isArray(d)
                            ? d.length
                            : d.count ?? results.length
                    );
                } else {
                    setPaymentPendingUsers([]);
                    setPaymentPendingUsersTotalCount(0);
                    setPaymentPendingUsersTotalPages(1);
                }
            } catch (error) {
                console.error(
                    "Error loading pending users:",
                    error
                );

                setPaymentPendingUsers([]);
                setPaymentPendingUsersTotalCount(0);
                setPaymentPendingUsersTotalPages(1);

                toast.error(
                    "Error loading pending users"
                );
            } finally {
                setLoading(false);
            }
        },
        [canSeePaymentPendingUsers]
    );

    /*
     * Load whenever page or search changes
     */
    useEffect(() => {
        loadPaymentPendingUsers(
            searchQuery,
            paymentPendingUsersPage
        );
    }, [
        loadPaymentPendingUsers,
        searchQuery,
        paymentPendingUsersPage,
    ]);

    /*
     * Reset pagination when searching
     */
    useEffect(() => {
        setPaymentPendingUsersPage(1);
    }, [searchQuery]);

    const handlePendingPageChange = (
        page: number
    ) => {
        if (
            page === paymentPendingUsersPage ||
            page < 1 ||
            page > paymentPendingUsersTotalPages
        ) {
            return;
        }

        setPaymentPendingUsersPage(page);
    };

    const clearFilters = () => {
        setSearchQuery("");
        setPaymentPendingUsersPage(1);
    };

    if (!canSeePaymentPendingUsers) {
        return null;
    }

    return (
        <div className="space-y-4">
            <AdminHeader/>
            {/* Search */}
            <div className="m-16">
            <div className="flex items-center justify-between">
                <div className="relative w-full max-w-md">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) =>
                            setSearchQuery(e.target.value)
                        }
                        placeholder="Search pending users..."
                        className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    />
                </div>

                {searchQuery && (
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="ml-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                    >
                        Clear
                    </button>
                )}
            </div>

            <PaymentPendingUserTable
                data={paymentPendingUsers}
                loading={loading}
                currentPage={paymentPendingUsersPage}
                totalPages={paymentPendingUsersTotalPages}
                totalCount={paymentPendingUsersTotalCount}
                onPageChange={handlePendingPageChange}
            />
            </div>
        </div>
    );
}